import type {
  AlcoveEdgeName,
  CornerName,
  EdgeName,
  FloorPlanCorner,
  FloorPlanRoom,
  LineEdgeRef,
  LineFrame,
  OuterWhich,
  Point,
} from '@kaiizen/kai-floor-plan';
import { DEFAULT_FLOOR_TYPE_OPTIONS, FloorTypeOption } from './editorConstants';
import type { InputKind } from './inputKind';
import { ALL_PLAN_LAYERS, type PlanLayers } from './planLayers';
import type { ValueTarget } from './valueTargets';
import { createViewport, Viewport } from './viewport';

// Re-exported so editor modules can keep importing them from the model.
// LineEdgeRef itself moved to @kaiizen/kai-floor-plan (Phase 2, state refactor).
export type { LineEdgeRef, OuterWhich };

export type Selection =
  | { kind: 'none' }
  /** The whole room, via a click on the bare floor: all four primary edges + corners light up. */
  | { kind: 'room' }
  | { kind: 'edge'; edge: EdgeName }
  | { kind: 'corner'; corner: CornerName }
  | { kind: 'alcove'; edge: EdgeName; index: number }
  | { kind: 'alcoveCorner'; edge: EdgeName; index: number; which: OuterWhich }
  | { kind: 'alcoveWall'; edge: EdgeName; index: number; wall: AlcoveEdgeName }
  | { kind: 'lineSeg'; ref: LineEdgeRef; si: number }
  | { kind: 'inner'; index: number }
  | { kind: 'innerCorner'; index: number; which: CornerName };

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MeasureBar {
  /** "h" = horizontal, "v" = vertical, "d" = diagonal (uses lf for geometry). */
  type: 'h' | 'v' | 'd';
  /** For h/v: world coordinate range along the bar. For d: position range along lf. */
  from: number;
  to: number;
  /** For h: model y; for v: model x; for d: unused (bar offset derived from lf + PAD). */
  at: number;
  value: number;
  key: string;
  /** Bold rendering — the center (size) measurement of a gap/size/gap triple. */
  emphasized?: boolean;
  /** Required when type === "d": the line frame of the diagonal edge. */
  lf?: LineFrame;
}

/** Everything needed to render + edit a chamfer, shared by room and alcove corners. */
export interface ChamferData {
  ch: FloorPlanCorner;
  corner: Point;
  sx: number;
  sy: number;
  frame: Rect;
  maxDx: number;
  maxDy: number;
}

/**
 * Unified perpendicular-drag config for ANY edge (primary wall, alcove wall,
 * corner diagonal, free-element side).
 *
 * delta = dot(pointerScreen - grabScreen, perp) / viewport.scale   (model units)
 * newValue = clamp(initialValue + delta, min, max)
 * applyValue(draft, target, newValue)
 *
 * Zero-jump guaranteed because delta = 0 at drag-start. `perp` is a model-space
 * unit vector; for a pure scale+translate viewport the screen-space dot product
 * equals the model-space one (scale cancels in the normalization).
 */
export interface PerpDrag {
  grabScreen: Point;
  perp: Point; // model-space unit vector: positive delta = value increases
  initialValue: number;
  min: number;
  max: number;
  /**
   * Set when the dragged edge is anchored in model space (its world position
   * does not move as the value grows — the origin-side room edges). The
   * viewport offset shifts by `perp · Δvalue · scale` so the grabbed edge
   * stays under the pointer. Derived from the frame, never from edge names.
   */
  viewportComp?: Point;
  initialOffset?: Point; // viewport.offset snapshot, required with viewportComp
}

/** The active drag, as a typed union — one shape per gesture family. */
export type Gesture =
  /** Perpendicular edge drag: one scalar target, clamped, applied each move. */
  | { kind: 'perp'; pd: PerpDrag; target: ValueTarget }
  /** Parallel slide/resize of a segment along its host line (any segment type, any host). */
  | {
    kind: 'slide';
    ref: LineEdgeRef;
    si: number;
    mode: 'move' | 'start' | 'end';
    range: { min: number; max: number };
    grabOffset: number;
    /** Fixed far end while dragging the start side (0 otherwise). */
    anchor: number;
  }
  /**
   * Two-axis chamfer corner-triangle drag. All chamfer math is in the host's
   * local (unrotated) frame; `local` — set when the host is a rotated free
   * element — inverse-rotates the pointer into that frame first.
   */
  | { kind: 'tri'; sx: number; sy: number; cx: number; cy: number; maxDx: number; maxDy: number; local?: { cx: number; cy: number; rad: number } }
  /** Free-element body translate. */
  | { kind: 'moveFree'; index: number; grabX: number; grabY: number }
  /** Free-element move from the 3D gadget: along an axis or within a plane, from where it began. */
  | { kind: 'moveFree3D'; index: number; start: { x: number; y: number; base: number } }
  /** Free-element rotation about its center via the rotation handle. */
  | { kind: 'rotateFree'; index: number; cx: number; cy: number };

export interface EditorMutableState {
  shape: FloorPlanRoom;
  selection: Selection;
  /** Center-stage view mode: 2D SVG plan editor, 3D dollhouse view, or JSON. */
  mode: '2d' | '3d' | 'json';
  viewport: Viewport;
  drag: Gesture | null;
  /**
   * The in-flight working copy during a drag. Gestures mutate ONLY this; on
   * pointer-up it is normalized and committed as the new `shape` — the single
   * commit path for every gesture.
   */
  draftShape: FloorPlanRoom | null;
  editingKey: string | null;
  edgeGap: { edge: EdgeName; gs: number; ge: number } | null;
  wallGap: { edge: EdgeName; storageIndex: number; wall: AlcoveEdgeName; gs: number; ge: number } | null;
  /** What shows inside the walls: built-ins, movable objects, both or neither. */
  layers: PlanLayers;
  addMenu: { x: number; y: number; edge: EdgeName } | null;
  /**
   * The allowed flooring types (host-overridable via the FloorPlanEditor
   * `floorTypes` prop). Every flooring pick list draws from this; option KEYS
   * are stored on the room, LABELS are presented.
   */
  floorTypeOptions: FloorTypeOption[];
  /**
   * Read-only mode (host-set, via the FloorPlanEditor `readOnly` prop):
   * everything stays selectable and inspectable, but no mutation path is
   * reachable — drags are discarded at pointer-down, deletes/adds and panel
   * edits are gated off. Optional for backward compatibility; absent means
   * editable.
   */
  readOnly?: boolean;
  /**
   * Whether a moved or side-resized free element snaps to nearby axis-aligned
   * edges (host-set from the preference, see elementSnapping). Absent means
   * no snapping.
   */
  snapping?: boolean;
  /**
   * Mouse or finger (host-set, see inputKind): the handles' reaches and the
   * chips' spacing follow it. Absent means a mouse.
   */
  inputKind?: InputKind;
}

/** The initial editor state for a room — pure; shared by the React hook and headless tests. */
export function createEditorState(
  shape: FloorPlanRoom,
  floorTypeOptions: FloorTypeOption[] = DEFAULT_FLOOR_TYPE_OPTIONS,
  readOnly = false,
  snapping = true,
  inputKind: InputKind = 'pointer',
): EditorMutableState {
  return {
    shape,
    readOnly,
    snapping,
    inputKind,
    selection: { kind: 'none' },
    mode: '2d',
    viewport: createViewport(),
    drag: null,
    draftShape: null,
    editingKey: null,
    edgeGap: null,
    wallGap: null,
    layers: ALL_PLAN_LAYERS,
    addMenu: null,
    floorTypeOptions,
  };
}
